<template>
    <div class="mb-0" v-tooltip.top="{ value: getTooltipText(), showDelay: 250, hideDelay: 300 }">
        <div class="form-check form-switch"
            :class="{ 'disabled-notification': !canUseWebPush || !canRequestWebPush }">
            <input type="checkbox" :class="['form-check-input', notificationsEnabled ? 'form-switch-danger' : '']"
                id="notificationToggle" :checked="notificationsEnabled" @change="toggleNotifications"
                :disabled="!canUseWebPush || !canRequestWebPush || loading">
            <label class="form-check-label"
                :class="[(!canUseWebPush || !canRequestWebPush) ? '' : 'cursor-pointer']" for="notificationToggle">
                <i v-if="!loading" class="fa fa-fw fa-bell me-2 d-inline-flex align-items-center justify-content-center"
                    :class="this.notificationsEnabled ? 'text-danger' : 'text-secondary'"
                    style="width: 1rem; height: 1rem;"></i>
                <div v-else
                    class="spinner-border spinner-border-sm text-danger me-2 d-inline-flex align-items-center justify-content-center"
                    style="width: 1rem; height: 1rem;" role="status">
                    <span class="visually-hidden">Loading...</span>
                </div>
                <strong v-if="explanation">Notify me when my phrase is ready</strong>
            </label>
        </div>
    </div>
</template>

<style>
.form-check-input.form-switch-danger:checked {
    background-color: #e86115;
    border-color: #e86115;
}

/* More intense graying out for disabled state */
.disabled-notification {
    opacity: 0.4;
    color: #999 !important;

    .form-check-label {
        color: #797878 !important;
    }

    i {
        color: #797878 !important;
    }
}
</style>
<script>

import {
    canUseWebPush,
    canRequestWebPush,
    requestWebPushPermission,
    removeSubscription,
    requestTestNotification,
    getCurrentSubscription
} from '../notifications.js';
import axios from 'axios';
import Swal from 'sweetalert2';
import Tooltip from 'primevue/tooltip';

export default {
    directives: {
        tooltip: Tooltip
    },
    props: {
        /**
         * If true, the component will not initialize itself automatically on mount.
         * You must call the 'initialize' method manually to set it up (with refs).
         */
        manualInit: {
            type: Boolean,
            default: false
        },
        explanation: {
            type: Boolean,
            default: true
        },
    },
    emits: [
        'notification-changed'
    ],
    data() {
        return {
            canUseWebPush: null,
            canRequestWebPush: null,
            notificationsEnabled: false,
            loading: true,
            initialized: false,
        };
    },
    mounted() {
        console.log("NotificationPreference component mounted.");

        if (!this.manualInit) {
            this.initialize();
        } else {
            console.log("NotificationPreference initialization waiting for initialization as 'manualInit' option was set. Call initialize() on this component when desired.");
        }
    },
    beforeUnmount() {
        console.log("NotificationPreference component before unmount.");
    },
    unmounted() {
        console.log("NotificationPreference component unmounted.");
    },
    methods: {
        async initialize() {
            if (this.initialized) {
                console.log("NotificationPreference component already initialized. Skipping.");
                return;
            }

            this.initialized = true;

            console.log("NotificationPreference component initializing.");
            //Check if webpush can be used on this browser
            this.canUseWebPush = canUseWebPush();

            //Check if we can request permission
            this.canRequestWebPush = canRequestWebPush();

            //Fetch current notification preference if applicable
            if (this.canUseWebPush && this.canRequestWebPush) {
                await this.getNotificationPreference();
            }

            this.loading = false;
        },
        async toggleNotifications(event) {
            //Don't allow toggling if disabled or loading
            if (!this.canUseWebPush || !this.canRequestWebPush || this.loading) {
                event.preventDefault();
                return;
            }

            //Store the intended state
            const intendedState = !this.notificationsEnabled;
            console.log('Notifications will be toggled to:', intendedState);

            this.loading = true;

            try {
                await this.saveNotificationPreference(intendedState);

                //Only now apply the intended state
                this.notificationsEnabled = intendedState;
                this.$emit('notification-changed', this.notificationsEnabled);

            } catch (error) {
                console.error('Failed to save notification preference:', error);
                // Toggle will stay in original position on error
            } finally {
                this.loading = false;
            }
        },

        getTooltipText() {
            if (this.loading) {
                return '';
            }

            if (!this.canUseWebPush) {
                return 'Your browser does not support web push notifications';
            }
            if (!this.canRequestWebPush) {
                return 'Notifications have been denied. Please check your browser settings to enable notifications for this site';
            }
            return 'Notify me when ready';
        },

        async getNotificationPreference() {
            console.log('Fetching notification preferences from API');

            /*
                +---------------------------+
                | Get browser subscription  |
                +------------+--------------+
                             |
                   +---------+---------+
                   |                   |
                   v                   v
            +-------------+    +-----------------+
            | Found       |    | Not found       |
            +------+------+    +--------+--------+
                   |                    |
                   |                    v
                   |            +---------------+
                   |            | END PROCESS   |
                   |            +---------------+
                   |
                   v
                +---------------------------+
                | Get API subscription      |
                +------------+--------------+
                             |
                   +---------+---------+
                   |                   |
                   v                   v
            +-------------+    +-----------------+
            | Found       |    | 404 Not found   |
            +------+------+    +--------+--------+
                   |                    |
                   |                    v
                   |            +---------------+
                   |            | END PROCESS   |
                   |            +---------------+
                   |
                   v
                +---------------------------+
                | Compare subscriptions     |
                +------------+--------------+
                             |
               +-------------+--------------+
               |                            |
               v                            v
+-------------------+      +---------------------------+
| No match found    |      | Match found               |
| - END PROCESS     |      | - Get notification prefs  |
+-------------------+      +---------------------------+
            */

            //Get webpush subscription from browser
            let browserSubscription = await getCurrentSubscription();
            if (!browserSubscription) {
                console.log('No browser subscription found. Skipping preference fetch.');
                return;
            }
            console.log('Browser subscription found:', browserSubscription);

            let apiSubscriptions = null;
            //Get webpush subscription from API
            await axios.get('/api/v1/me/subscriptions')
                .then(response => {
                    let data = response.data.data;
                    apiSubscriptions = data;
                    console.log('Subscription fetched from API:', apiSubscriptions);
                })
                .catch(error => {
                    if (error.response && error.response.status === 404) {
                        console.log('No subscription found (404), setting to null and stopping preference fetch.');
                        apiSubscriptions = null;
                        return;
                    } else {
                        console.error('Error fetching subscriptions from API:', error);
                        //Throw exception to trigger error tracking
                        throw error;
                    }
                });

            //Check if API subscription matches browser subscription
            console.log('Comparing browser and API subscriptions for preference fetch.');

            let matchFound = false;
            for (const apiSubscription of apiSubscriptions) {
                if (browserSubscription.endpoint === apiSubscription.endpoint) {
                    matchFound = true;
                    break;
                }
            }

            if (!matchFound) {
                //No matches found. End here, then user can resubscribe by enabling notifications
                console.log('No matching subscription found between browser and API. Skipping preference fetch.');
                return;
            }

            console.log('Matching subscription found. Proceeding to fetch notification preference.');

            //Check current permission status from API
            await axios.get('/api/v1/me/preferences')
                .then(response => {
                    let data = response.data.data;
                    if (data && data.tts_phrase_webpush !== undefined) {
                        this.notificationsEnabled = data.tts_phrase_webpush;
                        console.log('Notification preference fetched:', this.notificationsEnabled);
                    } else {
                        console.log('No notification preference found in API response.');
                    }
                })
                .catch(error => {
                    console.error('Error fetching notification preferences:', error);
                    //Throw exception to trigger error tracking
                    throw error;
                });
        },
        handleClick() {
            // Method that can be called from parent via ref to trigger the toggle
            const event = { preventDefault: () => {} };
            this.toggleNotifications(event);
        },

        async saveNotificationPreference(targetState) {
            //Request webpush permissions. If it is already granted, this will resolve immediately.
            await requestWebPushPermission().then(() => {
                console.log('Web push permission granted.');
            }).catch((error) => {
                console.error('Web push permission denied:', error);

                //Make a swal alert
                Swal.fire({
                    icon: 'error',
                    // title: 'Notifications permission was denied',
                    // text: 'Please enable Notifications Permission in your browser settings for this site to use this feature.',
                    title: 'Notifications permission',
                    text: error.message || 'Please enable Notifications Permission in your browser settings for this site to use this feature.',
                    theme: 'auto'
                });

                //Throw exception to trigger error tracking
                throw error;
            });

            //Save to API
            await axios.post('/api/v1/me/preferences', {
                tts_phrase_webpush: targetState
            }).then(response => {
                console.log('Notification preference saved successfully.');
            }).catch(error => {
                console.error('Error saving notification preferences:', error);

                //Make a swal alert
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'There was an error saving your notification preferences. Please try again later.',
                    theme: 'auto'
                });

                //Throw exception to trigger error tracking
                throw error;
            });
        }
    },
    watch: {
        initialValue(newVal) {
            this.notificationsEnabled = newVal;
        },
        triggerToggle(newVal) {
            if (newVal === true) {
                // Trigger the toggle when prop becomes true
                this.handleExternalToggle();
                // Emit an event to let parent know to reset the prop
                this.$emit('toggle-triggered');
            }
        }
    },
};
</script>
